import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  FileDownload as ExcelIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import LeaveRequestService, {
  LEAVE_TYPE_LABELS,
  ANNUAL_LEAVE_ENTITLEMENT_DAYS,
  ANNUAL_LEAVE_TYPES,
} from '../../services/leave-request.service';
import EmployeeService from '../../services/employee.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { addDarbReportHeader, PDF_TABLE_HEAD_STYLES } from '../../utils/pdfReportHeader';

interface EmployeeSummary {
  id: number;
  name: string;
  approved: number;
  rejected: number;
  pending: number;
  totalDaysApproved: number;
  annualDaysTaken: number;
  remainingBalance: number;
  requests: any[];
}

const getActionByName = (attrs: any): string => {
  if (attrs?.status === 'approved') {
    const approver = attrs?.approvedBy?.data?.attributes;
    return approver ? `${approver.firstName} ${approver.lastName}` : '-';
  }
  if (attrs?.status === 'rejected') {
    const rejector = attrs?.rejectedBy?.data?.attributes;
    return rejector ? `${rejector.firstName} ${rejector.lastName}` : '-';
  }
  return '-';
};

const LeaveReport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [employeeSummaries, setEmployeeSummaries] = useState<EmployeeSummary[]>([]);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [employeeFilter, setEmployeeFilter] = useState('all');

  const years = [
    new Date().getFullYear().toString(),
    (new Date().getFullYear() - 1).toString(),
    (new Date().getFullYear() - 2).toString(),
  ];

  useEffect(() => {
    fetchData();
  }, [yearFilter, statusFilter, leaveTypeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [leaveRes, empRes] = await Promise.all([
        LeaveRequestService.getAll({
          populate: ['employee', 'approvedBy', 'rejectedBy'],
          'pagination[pageSize]': 500,
          sort: 'createdAt:desc',
          'filters[startDate][$gte]': `${yearFilter}-01-01`,
          'filters[startDate][$lte]': `${yearFilter}-12-31`,
          ...(statusFilter !== 'all' && { 'filters[status][$eq]': statusFilter }),
          ...(leaveTypeFilter !== 'all' && { 'filters[leaveType][$eq]': leaveTypeFilter }),
        }),
        EmployeeService.getAll({
          'pagination[pageSize]': 500,
          'filters[status][$eq]': 'active',
          sort: 'firstName:asc',
        }),
      ]);

      const requests = leaveRes.data?.data || [];
      const employees = empRes.data?.data || [];
      setLeaveRequests(requests);
      setAllEmployees(employees);
      buildSummaries(requests, employees);
    } catch (err) {
      console.error('Error fetching leave data:', err);
      setError(t('leave.report.noData'));
    } finally {
      setLoading(false);
    }
  };

  const buildSummaries = (requests: any[], employees: any[]) => {
    const byEmployee = new Map<number, EmployeeSummary>();

    employees.forEach((emp) => {
      const attrs = emp.attributes;
      byEmployee.set(emp.id, {
        id: emp.id,
        name: `${attrs?.firstName || ''} ${attrs?.lastName || ''}`.trim(),
        approved: 0,
        rejected: 0,
        pending: 0,
        totalDaysApproved: 0,
        annualDaysTaken: 0,
        remainingBalance: ANNUAL_LEAVE_ENTITLEMENT_DAYS,
        requests: [],
      });
    });

    requests.forEach((req) => {
      const attrs = req.attributes;
      const empData = attrs?.employee?.data;
      if (!empData) return;
      const empId = empData.id;

      if (!byEmployee.has(empId)) {
        const empAttrs = empData.attributes;
        byEmployee.set(empId, {
          id: empId,
          name: `${empAttrs?.firstName || ''} ${empAttrs?.lastName || ''}`.trim(),
          approved: 0,
          rejected: 0,
          pending: 0,
          totalDaysApproved: 0,
          annualDaysTaken: 0,
          remainingBalance: ANNUAL_LEAVE_ENTITLEMENT_DAYS,
          requests: [],
        });
      }

      const summary = byEmployee.get(empId)!;
      summary.requests.push(req);
      if (attrs?.status === 'approved') {
        summary.approved += 1;
        summary.totalDaysApproved += attrs?.totalDays || 0;
        if (ANNUAL_LEAVE_TYPES.has(attrs?.leaveType)) {
          summary.annualDaysTaken += attrs?.totalDays || 0;
        }
      } else if (attrs?.status === 'rejected') {
        summary.rejected += 1;
      } else if (attrs?.status === 'pending') {
        summary.pending += 1;
      }
    });

    byEmployee.forEach((summary) => {
      summary.remainingBalance = Math.max(
        0,
        ANNUAL_LEAVE_ENTITLEMENT_DAYS - summary.annualDaysTaken
      );
    });

    setEmployeeSummaries(
      Array.from(byEmployee.values()).sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  const filteredRequests =
    employeeFilter === 'all'
      ? leaveRequests
      : leaveRequests.filter(
          (r) => r.attributes?.employee?.data?.id === parseInt(employeeFilter, 10)
        );

  const selectedEmployeeSummary =
    employeeFilter !== 'all'
      ? employeeSummaries.find((s) => s.id === parseInt(employeeFilter, 10))
      : null;

  const totalApproved = leaveRequests.filter((r) => r.attributes?.status === 'approved').length;
  const totalRejected = leaveRequests.filter((r) => r.attributes?.status === 'rejected').length;
  const totalPending = leaveRequests.filter((r) => r.attributes?.status === 'pending').length;
  const totalDays = leaveRequests
    .filter((r) => r.attributes?.status === 'approved')
    .reduce((sum, r) => sum + (r.attributes?.totalDays || 0), 0);
  const totalAnnualTaken = employeeSummaries.reduce((sum, s) => sum + s.annualDaysTaken, 0);

  const getStatusColor = (status: string): any => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'error';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF('landscape');
    const tableStartY = await addDarbReportHeader(doc, {
      title: `${t('leave.report.title')} — ${yearFilter}`,
      subtitleLines: [
        `Generated: ${new Date().toLocaleDateString('en-GB')}`,
        `Total: ${leaveRequests.length}   Approved: ${totalApproved}   Rejected: ${totalRejected}   Pending: ${totalPending}`,
        `Annual entitlement: ${ANNUAL_LEAVE_ENTITLEMENT_DAYS} days per employee`,
      ],
    });

    const summaryRows = employeeSummaries.map((s) => [
      s.name,
      s.requests.length.toString(),
      s.approved.toString(),
      s.rejected.toString(),
      s.pending.toString(),
      s.annualDaysTaken.toString(),
      s.remainingBalance.toString(),
      s.totalDaysApproved.toString(),
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [[
        t('leave.employee'),
        t('leave.report.totalRequests'),
        t('leave.approved'),
        t('leave.rejected'),
        t('leave.pending'),
        t('leave.report.annualTaken'),
        t('leave.report.remainingBalance'),
        t('leave.report.daysTaken'),
      ]],
      body: summaryRows,
      theme: 'grid',
      headStyles: PDF_TABLE_HEAD_STYLES,
      styles: { fontSize: 8, cellPadding: 2 },
    });

    const detailStartY = (doc as any).lastAutoTable.finalY + 10;

    const rows = leaveRequests.map((req) => {
      const attrs = req.attributes;
      const emp = attrs?.employee?.data?.attributes;
      return [
        emp ? `${emp.firstName} ${emp.lastName}` : '-',
        LEAVE_TYPE_LABELS[attrs?.leaveType] || attrs?.leaveType || '-',
        attrs?.startDate || '-',
        attrs?.endDate || '-',
        attrs?.totalDays?.toString() || '-',
        attrs?.status || '-',
        getActionByName(attrs),
        attrs?.reason || attrs?.rejectionReason || '-',
      ];
    });

    autoTable(doc, {
      startY: detailStartY,
      head: [[
        t('leave.employee'),
        t('leave.leaveType'),
        t('leave.startDate'),
        t('leave.endDate'),
        t('leave.days'),
        t('leave.status'),
        t('leave.actionBy'),
        t('leave.reason'),
      ]],
      body: rows,
      theme: 'grid',
      headStyles: PDF_TABLE_HEAD_STYLES,
      styles: { fontSize: 7, cellPadding: 2 },
    });

    doc.save(`Leave-Report-${yearFilter}.pdf`);
  };

  const handleExportExcel = () => {
    const summaryRows = employeeSummaries.map((s) => ({
      Employee: s.name,
      'Total Requests': s.requests.length,
      Approved: s.approved,
      Rejected: s.rejected,
      Pending: s.pending,
      'Annual Days Taken': s.annualDaysTaken,
      'Remaining Annual Balance': s.remainingBalance,
      'Total Approved Days (All Types)': s.totalDaysApproved,
    }));

    const detailRows = leaveRequests.map((req) => {
      const attrs = req.attributes;
      const emp = attrs?.employee?.data?.attributes;
      const approver = attrs?.approvedBy?.data?.attributes;
      const rejector = attrs?.rejectedBy?.data?.attributes;
      return {
        Employee: emp ? `${emp.firstName} ${emp.lastName}` : '-',
        'Leave Type': LEAVE_TYPE_LABELS[attrs?.leaveType] || attrs?.leaveType || '-',
        'Start Date': attrs?.startDate || '-',
        'End Date': attrs?.endDate || '-',
        Days: attrs?.totalDays ?? '-',
        Status: attrs?.status || '-',
        'Approved By': approver ? `${approver.firstName} ${approver.lastName}` : '-',
        'Rejected By': rejector ? `${rejector.firstName} ${rejector.lastName}` : '-',
        Reason: attrs?.reason || '-',
        'Rejection Reason': attrs?.rejectionReason || '-',
      };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Details');
    XLSX.writeFile(wb, `Leave-Report-${yearFilter}.xlsx`);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/leave-requests')}>
            {t('common.back', 'Back')}
          </Button>
          <Typography variant="h4">{t('leave.report.title')}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleExportPDF}>
            PDF
          </Button>
          <Button variant="outlined" startIcon={<ExcelIcon />} onClick={handleExportExcel}>
            Excel
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            select
            label={t('common.date', 'Year')}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <TextField
            select
            label={t('leave.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">{t('leave.allStatus')}</MenuItem>
            <MenuItem value="pending">{t('leave.pending')}</MenuItem>
            <MenuItem value="approved">{t('leave.approved')}</MenuItem>
            <MenuItem value="rejected">{t('leave.rejected')}</MenuItem>
          </TextField>
          <TextField
            select
            label={t('leave.leaveType')}
            value={leaveTypeFilter}
            onChange={(e) => setLeaveTypeFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">{t('common.all')}</MenuItem>
            {Object.entries(LEAVE_TYPE_LABELS)
              .filter(([val]) => !['vacation', 'sick'].includes(val))
              .map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
          </TextField>
          <TextField
            select
            label={t('leave.employee')}
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">{t('leave.report.selectEmployee')}</MenuItem>
            {allEmployees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id.toString()}>
                {emp.attributes?.firstName} {emp.attributes?.lastName}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
            {t('common.refresh', 'Refresh')}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          { label: t('leave.report.totalRequests'), value: leaveRequests.length, color: 'primary.main' },
          { label: t('leave.approved'), value: totalApproved, color: 'success.main' },
          { label: t('leave.rejected'), value: totalRejected, color: 'error.main' },
          { label: t('leave.pending'), value: totalPending, color: 'warning.main' },
          { label: t('leave.report.approvedDays'), value: totalDays, color: 'info.main' },
          { label: t('leave.report.annualDaysTaken'), value: totalAnnualTaken, color: 'secondary.main' },
        ].map((card) => (
          <Card key={card.label}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={24} /> : card.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {selectedEmployeeSummary && (
        <Paper sx={{ mb: 3, p: 2, bgcolor: 'primary.50' }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Typography variant="h6">{selectedEmployeeSummary.name}</Typography>
            <Chip label={`${t('leave.report.remainingBalance')}: ${selectedEmployeeSummary.remainingBalance}/${ANNUAL_LEAVE_ENTITLEMENT_DAYS}`} color="primary" />
            <Chip label={`${t('leave.approved')}: ${selectedEmployeeSummary.approved}`} color="success" size="small" />
            <Chip label={`${t('leave.rejected')}: ${selectedEmployeeSummary.rejected}`} color="error" size="small" />
            <Chip label={`${t('leave.pending')}: ${selectedEmployeeSummary.pending}`} color="warning" size="small" />
          </Stack>
        </Paper>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{t('leave.report.employeeSummary')}</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>{t('leave.employee')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.report.totalRequests')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.approved')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.rejected')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.pending')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.report.daysTaken')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.report.annualTaken')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.report.remainingBalance')}</strong></TableCell>
                <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : employeeSummaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">{t('leave.report.noData')}</TableCell>
                </TableRow>
              ) : (
                employeeSummaries.map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    selected={employeeFilter === s.id.toString()}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setEmployeeFilter(s.id.toString())}
                  >
                    <TableCell><strong>{s.name}</strong></TableCell>
                    <TableCell align="center">{s.requests.length}</TableCell>
                    <TableCell align="center">
                      <Chip label={s.approved} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={s.rejected} color="error" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={s.pending} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <strong>{s.totalDaysApproved}</strong> {t('leave.daysUnit')}
                    </TableCell>
                    <TableCell align="center">{s.annualDaysTaken} {t('leave.daysUnit')}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${s.remainingBalance} / ${ANNUAL_LEAVE_ENTITLEMENT_DAYS}`}
                        color={s.remainingBalance <= 5 ? 'warning' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('leave.report.viewHistory')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEmployeeFilter(s.id.toString());
                          }}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">
            {employeeFilter !== 'all'
              ? `${t('leave.report.employeeHistory')} (${filteredRequests.length})`
              : `${t('leave.report.allRequests')} (${leaveRequests.length})`}
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>{t('leave.employee')}</strong></TableCell>
                <TableCell><strong>{t('leave.leaveType')}</strong></TableCell>
                <TableCell><strong>{t('leave.startDate')}</strong></TableCell>
                <TableCell><strong>{t('leave.endDate')}</strong></TableCell>
                <TableCell align="center"><strong>{t('leave.days')}</strong></TableCell>
                <TableCell><strong>{t('leave.status')}</strong></TableCell>
                <TableCell><strong>{t('leave.actionBy')}</strong></TableCell>
                <TableCell><strong>{t('leave.reason')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {employeeFilter !== 'all'
                      ? t('leave.report.noHistory')
                      : t('leave.noRequests')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((req) => {
                  const attrs = req.attributes;
                  const emp = attrs?.employee?.data?.attributes;
                  const actionBy = getActionByName(attrs);
                  return (
                    <TableRow key={req.id} hover>
                      <TableCell>{emp ? `${emp.firstName} ${emp.lastName}` : '-'}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {LEAVE_TYPE_LABELS[attrs?.leaveType] || attrs?.leaveType || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{attrs?.startDate || '-'}</TableCell>
                      <TableCell>{attrs?.endDate || '-'}</TableCell>
                      <TableCell align="center"><strong>{attrs?.totalDays ?? '-'}</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={t(`leave.${attrs?.status || 'pending'}`)}
                          color={getStatusColor(attrs?.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {actionBy !== '-' ? (
                          <Typography variant="body2" fontWeight="medium">{actionBy}</Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" noWrap title={attrs?.reason || attrs?.rejectionReason}>
                          {attrs?.reason || (attrs?.rejectionReason ? `${t('leave.rejectionReason')}: ${attrs.rejectionReason}` : '-')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default LeaveReport;
